import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client';

import * as tokenStore from 'src/utils/auth/tokenStore';
import { STORAGE_KEYS } from 'src/utils/auth/config';
import { emitSignedIn, emitSignedOut } from 'src/utils/auth/authEvents';
import {
  GET_VIEWER_CUSTOMER,
  LOGIN,
  REGISTER_CUSTOMER,
} from 'src/utils/account/operations';
import { AuthStatus, Customer } from 'src/utils/account/types';

const GENERIC_LOGIN_ERROR =
  'Neteisingas el. pašto adresas arba slaptažodis. Bandykite dar kartą.';

export interface RegisterInput {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

interface AuthState {
  status: AuthStatus;
  customer: Customer | null;
  login: (username: string, password: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  reloadCustomer: () => Promise<void>;
  /**
   * Confirms the signed-in customer's current password.
   *
   * updateCustomer accepts a new `password` without checking the old one, so this is
   * what stops a hijacked token from silently changing it. Runs the login mutation and
   * adopts the fresh tokens it returns — which also invalidates anything an attacker
   * might be holding — but deliberately emits no sign-in event, so a password change
   * does not kick off a cart merge.
   */
  verifyPassword: (password: string) => Promise<boolean>;
  setCustomer: (customer: Customer | null) => void;
}

const defaultState: AuthState = {
  status: 'ANONYMOUS',
  customer: null,
  login: () => {
    throw new Error('login called outside of AuthProvider');
  },
  register: () => {
    throw new Error('register called outside of AuthProvider');
  },
  logout: () => {
    throw new Error('logout called outside of AuthProvider');
  },
  reloadCustomer: () => {
    throw new Error('reloadCustomer called outside of AuthProvider');
  },
  verifyPassword: () => {
    throw new Error('verifyPassword called outside of AuthProvider');
  },
  setCustomer: () => {
    throw new Error('setCustomer called outside of AuthProvider');
  },
};

const AuthContext = createContext<AuthState>(defaultState);

interface AuthProviderProps {
  children: ReactNode;
  /** Test seam only — lets tests mount straight into a given state. */
  initialStatus?: AuthStatus;
  initialCustomer?: Customer | null;
}

const AuthProvider: FC<AuthProviderProps> = ({
  children,
  initialStatus,
  initialCustomer = null,
}) => {
  const client = useApolloClient();

  /**
   * Lazy initializer, so a returning customer boots straight into INITIALISING and
   * never renders an ANONYMOUS frame before the refresh resolves. Consumers show a
   * spinner for INITIALISING rather than a signed-out UI.
   */
  const [status, setStatus] = useState<AuthStatus>(
    () =>
      initialStatus ??
      (tokenStore.hasRecoverableSession() ? 'INITIALISING' : 'ANONYMOUS')
  );
  const [customer, setCustomer] = useState<Customer | null>(initialCustomer);

  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const applySignedOut = useCallback(() => {
    tokenStore.clearSession();

    if (isMounted.current) {
      setCustomer(null);
      setStatus('ANONYMOUS');
    }

    emitSignedOut();
  }, []);

  const fetchCustomer = useCallback(async (): Promise<Customer | null> => {
    const { data } = await client.query({
      query: GET_VIEWER_CUSTOMER,
      fetchPolicy: 'network-only',
    });

    return data?.customer ?? null;
  }, [client]);

  const reloadCustomer = useCallback(async () => {
    const next = await fetchCustomer();

    if (isMounted.current) {
      setCustomer(next);
    }
  }, [fetchCustomer]);

  // Restore a stored session on boot. Skipped when initialStatus was supplied, so a
  // test mounting straight into a state isn't raced by the restore logic.
  useEffect(() => {
    if (initialStatus !== undefined || status !== 'INITIALISING') {
      return;
    }

    let cancelled = false;

    (async () => {
      const token = await tokenStore.refresh();

      if (cancelled) {
        return;
      }

      if (!token) {
        applySignedOut();

        return;
      }

      try {
        const next = await fetchCustomer();

        if (cancelled) {
          return;
        }

        if (next) {
          setCustomer(next);
          setStatus('AUTHENTICATED');
        } else {
          applySignedOut();
        }
      } catch (error) {
        console.error('Could not restore session:', error);

        if (!cancelled) {
          applySignedOut();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // Runs once for the initial INITIALISING state; later transitions are explicit.
  }, []);

  // errorLink signs out from outside React when a token is unrecoverable.
  useEffect(() => {
    tokenStore.setUnauthenticatedHandler(() => {
      if (isMounted.current) {
        setCustomer(null);
        setStatus('ANONYMOUS');
      }

      emitSignedOut();
    });

    return () => tokenStore.setUnauthenticatedHandler(null);
  }, []);

  /**
   * Cross-tab sync. Covers signing out in one tab with another open on an account
   * page, and signing in as a different customer on a shared device.
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEYS.AUTH_HINT) {
        return;
      }

      if (!event.newValue) {
        setCustomer(null);
        setStatus('ANONYMOUS');

        return;
      }

      // Signed in elsewhere — pick up that session here too.
      tokenStore.refresh().then(token => {
        if (!token || !isMounted.current) {
          return;
        }

        fetchCustomer()
          .then(next => {
            if (next && isMounted.current) {
              setCustomer(next);
              setStatus('AUTHENTICATED');
            }
          })
          .catch(error => console.error('Cross-tab sync failed:', error));
      });
    };

    window.addEventListener('storage', handleStorage);

    return () => window.removeEventListener('storage', handleStorage);
  }, [fetchCustomer]);

  const login = useCallback(
    async (username: string, password: string) => {
      const guestCart = (() => {
        try {
          const raw = window.localStorage.getItem('cart');

          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })();

      let data;

      try {
        ({ data } = await client.mutate({
          mutation: LOGIN,
          variables: { username, password },
        }));
      } catch (error) {
        // Deliberately generic: never reveal whether the address exists.
        throw new Error(GENERIC_LOGIN_ERROR);
      }

      const payload = data?.login;

      if (!payload?.authToken || !payload?.refreshToken) {
        throw new Error(GENERIC_LOGIN_ERROR);
      }

      tokenStore.setSession({
        authToken: payload.authToken,
        authTokenExpiration: Number(payload.authTokenExpiration),
        refreshToken: payload.refreshToken,
        refreshTokenExpiration: Number(payload.refreshTokenExpiration),
      });

      const wooSessionToken = payload.customer?.auth?.wooSessionToken ?? null;

      if (wooSessionToken) {
        // Set explicitly rather than waiting for wooSessionAfterware, so the very next
        // request already carries the customer's own Woo session.
        window.localStorage.setItem(STORAGE_KEYS.WOO_SESSION, wooSessionToken);
      }

      setCustomer(payload.customer ?? null);
      setStatus('AUTHENTICATED');

      emitSignedIn({ guestCart, wooSessionToken });
    },
    [client]
  );

  const register = useCallback(
    async (input: RegisterInput) => {
      const { data } = await client.mutate({
        mutation: REGISTER_CUSTOMER,
        variables: {
          input: {
            email: input.email,
            username: input.email,
            password: input.password,
            firstName: input.firstName,
            lastName: input.lastName,
          },
        },
      });

      const newCustomer = data?.registerCustomer?.customer;
      const auth = newCustomer?.auth;

      // registerCustomer's own payload carries no tokens, but customer.auth does, so
      // registration signs the person in without a second round-trip. If a future
      // plugin version stops populating it, fall back to an explicit login.
      if (auth?.authToken && auth?.refreshToken) {
        tokenStore.setSession({
          authToken: auth.authToken,
          authTokenExpiration: Number(auth.authTokenExpiration),
          refreshToken: auth.refreshToken,
          refreshTokenExpiration: Number(auth.refreshTokenExpiration),
        });

        if (auth.wooSessionToken) {
          window.localStorage.setItem(
            STORAGE_KEYS.WOO_SESSION,
            auth.wooSessionToken
          );
        }

        setCustomer(newCustomer);
        setStatus('AUTHENTICATED');

        emitSignedIn({
          guestCart: null,
          wooSessionToken: auth.wooSessionToken ?? null,
        });

        return;
      }

      await login(input.email, input.password);
    },
    [client, login]
  );

  const verifyPassword = useCallback(
    async (password: string): Promise<boolean> => {
      const username = customer?.username ?? customer?.email;

      if (!username) {
        return false;
      }

      try {
        const { data } = await client.mutate({
          mutation: LOGIN,
          variables: { username, password },
        });

        const payload = data?.login;

        if (!payload?.authToken || !payload?.refreshToken) {
          return false;
        }

        // Adopt the fresh tokens; no emitSignedIn, so nothing downstream reacts as if
        // this were a new sign-in.
        tokenStore.setSession({
          authToken: payload.authToken,
          authTokenExpiration: Number(payload.authTokenExpiration),
          refreshToken: payload.refreshToken,
          refreshTokenExpiration: Number(payload.refreshTokenExpiration),
        });

        return true;
      } catch {
        return false;
      }
    },
    [client, customer]
  );

  const logout = useCallback(async () => {
    // There is no logout mutation on this backend — sign-out is local token disposal.
    applySignedOut();

    // clearStore, not resetStore: resetStore refetches active queries, which would
    // immediately re-run the customer/orders queries with no token.
    await client.clearStore();
  }, [applySignedOut, client]);

  const value = useMemo(
    () => ({
      status,
      customer,
      login,
      register,
      logout,
      reloadCustomer,
      verifyPassword,
      setCustomer,
    }),
    [status, customer, login, register, logout, reloadCustomer, verifyPassword]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthState => useContext(AuthContext);

export default AuthProvider;
